<template>
    <div class="application-form-wrapper p-3 p-xl-4 min-vh-100">
        <b-container>
            <div class="d-flex justify-content-lg-between justify-content-center align-items-center mb-4">
                <h3 class="mb-0 font-weight-bold d-none d-lg-block">{{ $t('visitor_student_application_form') }}</h3>
                <div class="d-flex gap-2">
                    <b-button class="btn-40 d-lg-flex shadow-sm" variant="outline-secondary"
                              @click="changeLocale('en')" v-if="locale === 'tr'">
                        EN
                    </b-button>
                    <b-button class="btn-40 d-lg-flex shadow-sm" variant="outline-secondary"
                              @click="changeLocale('tr')" v-else>
                        TR
                    </b-button>
                    <b-button variant="outline-primary">
                        <CountDown
                            :localStorageKey="'visitorStudentSessionDeadline'"
                            deadline="2026-12-31 23:59:59"
                            :showDays="false"
                            :show-hours="false"
                            ref="countdown"
                            customClass="text-primary font-weight-bold"
                            @expired="onCountdownExpired"
                        />
                    </b-button>
                    <b-button variant="outline-danger" @click="logout">
                        <i class="ri-logout-circle-line mr-1 top-minus-1 align-middle"></i>
                        {{ $t('logout') }}
                    </b-button>
                </div>
            </div>
            <div class="d-lg-none mb-2">
                <h3 class="mb-0 font-weight-bold">{{ $t('visitor_student_application_form') }}</h3>
            </div>
            <b-card no-body class="shadow-sm p-3">
                <b-tabs v-model="tabIndex">
                    <b-tab :title="$t('information')">
                        <InfoTab :user-data="user" @next="(data) => goToNextTab('info', data)" />
                    </b-tab>
                    <b-tab :title="$t('program_select')" :disabled="maxTabIndex < 1">
                        <ProgramSelectionTab :user-data="user" :initial-program="formData.program" @next="(data) => goToNextTab('program', data)" @prev="prevTab" />
                    </b-tab>
                    <b-tab :title="$t('course_registration')" :disabled="maxTabIndex < 2">
                        <CourseRegistrationTab
                            :user-data="user"
                            :is-graduate-level="isGraduate"
                            :program-level="formData.programLevel"
                            :program-code="formData.program && formData.program.appliedProgram ? formData.program.appliedProgram.program : null"
                            :faculty-code="formData.program && formData.program.appliedProgram ? formData.program.appliedProgram.faculty : null"
                            :graduate-type="formData.program && formData.program.appliedProgram ? formData.program.appliedProgram.thesis : null"
                            :initial-courses="formData.courses && formData.courses.courses ? formData.courses.courses : []"
                            @next="(data) => goToNextTab('courses', data)"
                            @prev="prevTab"
                        />
                    </b-tab>
                    <b-tab :title="$t('documents')" :disabled="maxTabIndex < 3">
                        <DocumentsTab
                            :user-data="user"
                            :program-level="formData.programLevel"
                            :server-documents="formData.serverDocuments || []"
                            @next="(data) => goToNextTab('documents', data)"
                            @prev="prevTab"
                        />
                    </b-tab>
                    <b-tab :title="$t('summary')" :disabled="maxTabIndex < 4">
                        <SummaryTab
                            :user-data="user"
                            :form-data="formData"
                            @prev="prevTab"
                            @submit="onSubmit"
                        />
                    </b-tab>
                </b-tabs>
            </b-card>
        </b-container>

        <CommonModal ref="successModal" size="md" :onHideOnlyX="true" :closeBtn="false" @bHideModalHeaderClose="goToLogin">
            <template v-slot:CommonModalTitle>
                <div class="d-flex align-items-center" style="color: #28a745;">
                    <i class="ri-checkbox-circle-line mr-2" style="font-size: 1.5rem;"></i>
                    {{ $t('application_submitted_success') }}
                </div>
            </template>
            <template v-slot:CommonModalContent>
                <div class="text-center py-3 d-flex flex-column align-items-center">
                    <div class="text-muted font-size-13 mb-4" style="line-height: 1.7;">
                        {{ $t('application_submitted_info') }}
                    </div>
                    <b-button
                        variant="success"
                        class="font-weight-bold shadow-sm px-5 rounded-pill py-2"
                        @click="goToLogin"
                    >
                        {{ $t('ok').toUpperCase() }}
                    </b-button>
                </div>
            </template>
        </CommonModal>
    </div>
</template>

<script>
import InfoTab from "../components/tabs/InfoTab.vue";
import ProgramSelectionTab from "../components/tabs/ProgramSelectionTab.vue";
import CourseRegistrationTab from "../components/tabs/CourseRegistrationTab.vue";
import DocumentsTab from "../components/tabs/DocumentsTab.vue";
import SummaryTab from "../components/tabs/SummaryTab.vue";
import CommonModal from "@/components/elements/CommonModal.vue";
import CountDown from "@/components/elements/CountDown.vue";
import { localeChanged } from 'vee-validate';

export default {
    components: {
        InfoTab,
        ProgramSelectionTab,
        CourseRegistrationTab,
        DocumentsTab,
        SummaryTab,
        CommonModal,
        CountDown
    },
    computed: {
        locale() {
            return this.$store.getters['getLocale'] || this.$i18n.locale;
        },
        tabIndex: {
            get() {
                return this.$store.state.VisitorStudentApplicationStore.tabIndex;
            },
            set(val) {
                this.$store.commit('VisitorStudentApplicationStore/updateTabIndex', val);
            }
        },
        maxTabIndex() {
            return this.$store.state.VisitorStudentApplicationStore.maxTabIndex;
        },
        formData() {
            return this.$store.state.VisitorStudentApplicationStore.tempForm;
        },
        user() {
            return this.$store.getters['VisitorStudentApplicationStore/getUser'] || {};
        },
        isGraduate() {
            return ['YL', 'D'].includes(this.formData.programLevel);
        }
    },
    mounted() {
        if (!this.$store.getters['VisitorStudentApplicationStore/isLoggedIn']) {
            this.$router.push('/visitor-student/application/login');
        }
    },
    methods: {
        changeLocale(locale) {
            this.$i18n.locale = locale;
            this.$store.commit('setLocale', locale);
            localeChanged();
        },
        logout() {
            this.$store.dispatch('VisitorStudentApplicationStore/logout');
            this.$router.push('/visitor-student/application/login');
        },
        onCountdownExpired() {
            this.$store.dispatch('VisitorStudentApplicationStore/logout');
        },
        goToNextTab(section, data) {
            if (!data) return;

            const newFormData = { ...this.formData, [section]: data };
            if (section === 'program' && data.appliedProgram) {
                newFormData.programLevel = data.appliedProgram.level;
            }
            this.$store.commit('VisitorStudentApplicationStore/updateTempForm', newFormData);

            const nextIndex = this.tabIndex + 1;
            if (nextIndex > this.maxTabIndex) {
                this.$store.commit('VisitorStudentApplicationStore/updateMaxTabIndex', nextIndex);
            }

            this.$nextTick(() => {
                this.$store.commit('VisitorStudentApplicationStore/updateTabIndex', nextIndex);
            });
        },
        prevTab() {
            if (this.tabIndex > 0) {
                this.$store.commit('VisitorStudentApplicationStore/updateTabIndex', this.tabIndex - 1);
            }
        },
        onSubmit() {
            this.$refs.successModal.$refs.commonModal.show()
        },
        goToLogin() {
            this.$refs.successModal.$refs.commonModal.hide()
            this.$store.dispatch('VisitorStudentApplicationStore/logout');
            this.$router.push('/visitor-student/application/login');
        }
    }
};
</script>
